import React, { useEffect, useReducer, useState } from "react";
import {
  Box,
  Button,
  CardMedia,
  Chip,
  Divider,
  FormLabel,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import Reducer from "../../../services/Reducer";
import LoadingScreen from "../../../components/LoadingScreen";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import {
  GetEventDetails,
  GetEventListHandler,
} from "../../../actions/mainAction";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { MusicNote, MusicNoteOutlined } from "@mui/icons-material";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import PropTypes from "prop-types";

const initState = {
  loading: false,
  event: null,
  eventListData: null,
  detailedEventData: null,
  error: null,
};

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export default function EventDetails() {
  const [state, dispatch] = useReducer(Reducer, initState);
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [value, setValue] = React.useState(0);
  const [countdown, setCountdown] = useState({
    hours: 1,
    minutes: 30,
    seconds: 45,
  });

  // Debug: Log countdown state changes
  useEffect(() => {
    // console.log('Countdown state updated:', countdown);
  }, [countdown]);

  // Test countdown timer (for debugging)
  useEffect(() => {
    const testInterval = setInterval(() => {
      setCountdown((prev) => {
        let { hours, minutes, seconds } = prev;

        if (seconds > 0) {
          seconds--;
        } else if (minutes > 0) {
          minutes--;
          seconds = 59;
        } else if (hours > 0) {
          hours--;
          minutes = 59;
          seconds = 59;
        }

        return { hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(testInterval);
  }, []);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
    const fromState = location.state?.event;
    const eventId = params.get("id");

    if (!eventId) {
      navigate("/user/event-list");
      return;
    }

    // Store event list data if available
    if (fromState) {
      dispatch({
        type: "SETDATA",
        payload: { name: "eventListData", value: fromState },
      });
    }

    // Always fetch detailed event data
    fetchEventDetails(eventId);
  }, [params, location.state, navigate]);

  const fetchEventDetails = async (id) => {
    dispatch({ type: "LOAD", payload: true });
    dispatch({ type: "SETDATA", payload: { name: "error", value: null } });

    try {
      const response = await GetEventDetails(id);
      if (response && response.status === 200) {
        // Store detailed event data
        dispatch({
          type: "SETDATA",
          payload: { name: "detailedEventData", value: response.data },
        });
      } else {
        const errorMessage =
          response?.data?.message || "Failed to fetch event details";
        dispatch({
          type: "SETDATA",
          payload: { name: "error", value: errorMessage },
        });
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        "An error occurred while fetching event details";
      dispatch({
        type: "SETDATA",
        payload: { name: "error", value: errorMessage },
      });
    } finally {
      dispatch({ type: "LOAD", payload: false });
    }
  };

  // Effect to merge data when both sources are available
  useEffect(() => {
    const eventListData = state.eventListData;
    const detailedData = state.detailedEventData;

    // If we have both data sources, merge them
    if (eventListData && detailedData) {
      const mergedEvent = {
        ...eventListData, // Start with event list data
        ...detailedData, // Override with detailed data where available
        // Keep some specific fields from event list if they're not in detailed data
        eventName: detailedData.eventName || eventListData.eventName,
        eventAddress: detailedData.eventAddress || eventListData.eventAddress,
        startTime: detailedData.startTime || eventListData.startTime,
        endTime: detailedData.endTime || eventListData.endTime,
        price: detailedData.price || eventListData.price,
        logo: detailedData.logo || eventListData.logo,
      };
      dispatch({
        type: "SETDATA",
        payload: { name: "event", value: mergedEvent },
      });
    } else if (detailedData) {
      // If only detailed data is available
      dispatch({
        type: "SETDATA",
        payload: { name: "event", value: detailedData },
      });
    } else if (eventListData) {
      // If only event list data is available
      dispatch({
        type: "SETDATA",
        payload: { name: "event", value: eventListData },
      });
    }
  }, [state.eventListData, state.detailedEventData]);

  // Countdown timer effect - shows time remaining until event ends
  useEffect(() => {
    const calculateCountdown = () => {
      if (!state.event?.endTime) {
        // console.log('No endTime available for countdown');
        return;
      }

      console.log("Event data for countdown:", {
        eventName: state.event.eventName,
        startTime: state.event.startTime,
        endTime: state.event.endTime,
        currentTime: moment().format(),
      });

      const now = moment();
      const endTime = moment(state.event.endTime);

      console.log("Time comparison:", {
        now: now.format(),
        endTime: endTime.format(),
        isAfter: now.isAfter(endTime),
      });

      // Check if event has already ended
      if (now.isAfter(endTime)) {
        console.log("Event has ended, setting countdown to 0");
        setCountdown({ hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      // Calculate time remaining until event ends
      const duration = moment.duration(endTime.diff(now));
      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();
      const seconds = duration.seconds();

      console.log("Calculated countdown:", { hours, minutes, seconds });
      setCountdown({ hours, minutes, seconds });
    };

    // Calculate immediately
    calculateCountdown();

    // Update every second
    const interval = setInterval(calculateCountdown, 1000);

    return () => clearInterval(interval);
  }, [state.event?.endTime]);

  const e = state.event;
  console.log(e);
  return (
    <div className="listingPages">
      {state.loading && <LoadingScreen />}
      {!state.loading && e && (
        <Box maxWidth={1600} margin={"0 auto"}>
          {/* <Box borderRadius={'10px'} py={2} px={3} sx={{ backgroundColor: '#112E55' }} mb={2}>
                        <Typography fontSize={20} color={'#fff'} marginBottom={2} fontWeight={600}>Event Details</Typography>
                        <Divider sx={{ borderColor: '#0057FF', opacity: 1 }} />
                        <Box marginTop={3} marginBottom={3}>
                            <Grid container spacing={{ xs: 1, sm: 2 }}>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Grid item container alignItems={'flex-start'} gap={2}>
                                        <Grid item paddingTop={1}>
                                            <div className='img_block' style={{ width: 64, height: 64 }}>
                                                <img src={`${process.env.REACT_APP_S3_URL}${e?.logo}`} alt='' style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6 }} />
                                            </div>
                                        </Grid>
                                        <Grid item width={'calc(100% - 46px)'}>
                                            <Typography color={'#0057FF'} fontSize={14}>Event</Typography>
                                            <Typography color={'#fff'}>{e?.eventName || '-'}</Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Grid item container alignItems={'flex-start'} gap={2}>
                                        <Grid item paddingTop={1}>
                                            <img src='/images/pin-map.svg' alt='' width={30} />
                                        </Grid>
                                        <Grid item width={'calc(100% - 46px)'}>
                                            <Typography color={'#0057FF'} fontSize={14}>Address</Typography>
                                            <Typography color={'#fff'}>{e?.eventAddress || '-'}</Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Grid item container alignItems={'flex-start'} gap={2}>
                                        <Grid item paddingTop={1}>
                                            <img src='/images/clock.svg' alt='' width={30} />
                                        </Grid>
                                        <Grid item width={'calc(100% - 46px)'}>
                                            <Typography color={'#0057FF'} fontSize={14}>Date & Time</Typography>
                                            <Typography color={'#fff'}>{e?.eventDate ? moment(e?.eventDate).format('MMMM Do YYYY, h:mm:ss a') : '-'}</Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box> */}
          <Box width={"100%"} height={500} overflow={"hidden"} borderRadius={3}>
            <CardMedia
              component={"img"}
              src={`${process.env.REACT_APP_S3_URL}${e?.data?.logo}`}
              alt=""
            />
          </Box>
          <Box
            maxWidth={1000}
            mx={"auto"}
            border={1}
            borderColor={"#33588A"}
            borderRadius={"10px"}
            p={3}
            sx={{ backgroundColor: "#112E55" }}
            mt={"-100px"}
            position={"relative"}
            zIndex={2}
          >
            <Typography fontSize={24} fontWeight={600} color={"#fff"} mb={2}>
              {e?.data?.eventName || "-"}
            </Typography>
            {/* Data source indicator */}
            {/* <Box mb={2}>
                            <Typography fontSize={12} color={'#BAD3F5'}>
                                Data Sources: 
                                {state.eventListData && <span style={{color: '#4CAF50', marginLeft: '8px'}}>✓ Event List API</span>}
                                {state.detailedEventData && <span style={{color: '#4CAF50', marginLeft: '8px'}}>✓ Detailed API</span>}
                                {!state.eventListData && !state.detailedEventData && <span style={{color: '#ff6b6b', marginLeft: '8px'}}>No data available</span>}
                            </Typography>
                        </Box> */}
            <Box mb={1}>
              <Typography fontSize={12} color={"#BAD3F5"}>
                Event Status:
                <span style={{ color: "#4CAF50", marginLeft: "8px" }}>
                  {e?.data?.eventStatus}
                </span>
              </Typography>
            </Box>
            <Box mb={2}>
              <Typography fontSize={12} color={"#BAD3F5"}>
                Event Organizer:
                <span style={{ color: "#4CAF50", marginLeft: "8px" }}>
                  {`${e?.data?.userDetail?.firstName || ""} ${
                    e?.data?.userDetail?.lastName || ""
                  }`}
                </span>
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item width={"calc(100% - 280px)"}>
                <Grid item container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormLabel
                      sx={{
                        color: "#BAD3F5",
                        fontSize: 14,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <AccessTimeRoundedIcon fontSize="small" /> Start Date And
                      Time
                    </FormLabel>
                    <Typography color={"#fff"} fontSize={14} fontWeight={500}>
                      {e?.data?.startTime
                        ? moment(e?.data?.startTime).format(
                            "MMMM Do YYYY, h:mm:ss a"
                          )
                        : "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormLabel
                      sx={{
                        color: "#BAD3F5",
                        fontSize: 14,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <AccessTimeRoundedIcon fontSize="small" /> End Date And
                      Time
                    </FormLabel>
                    <Typography color={"#fff"} fontSize={14} fontWeight={500}>
                      {e?.data?.endTime
                        ? moment(e?.data?.endTime).format(
                            "MMMM Do YYYY, h:mm:ss a"
                          )
                        : "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormLabel
                      sx={{
                        color: "#BAD3F5",
                        fontSize: 14,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <LocationOnOutlinedIcon fontSize="small" /> Location
                    </FormLabel>
                    <Typography color={"#fff"} fontSize={14} fontWeight={500}>
                      {e?.data?.eventAddress || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormLabel
                      sx={{
                        color: "#BAD3F5",
                        fontSize: 14,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <AccountBalanceOutlinedIcon fontSize="small" /> Entry Fees
                    </FormLabel>
                    <Typography color={"#fff"} fontSize={20} fontWeight={500}>
                      ${e?.data?.price || "0.00"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormLabel
                      sx={{
                        color: "#BAD3F5",
                        fontSize: 14,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <MusicNoteOutlined fontSize="small" /> Music Genre
                    </FormLabel>
                    <Grid container item spacing={1}>
                      {e?.data?.eventGenreDetail?.map((genre, index) => (
                                                <Grid item key={index}>
                                                    <Chip label={genre?.genreDetail?.name || genre?.genreName || 'Unknown'} sx={{ backgroundColor: '#0057FF', color: '#fff' }} />
                                                </Grid>
                                            ))}
                      {/* {e?.data?.["eventGenreDetail.genreDetail.name"] ? (
                        <Grid>
                          <Chip
                            label={
                              e?.data["eventGenreDetail.genreDetail.name"] ||
                              "Unknown"
                            }
                            sx={{ backgroundColor: "#0057FF", color: "#fff" }}
                          />
                        </Grid>
                      ) : (
                        <></>
                      )}
                      } */}
                    </Grid>
                  </Grid>
                  {e.data?.eventStatus == "Upcoming" && (
                    <Grid item xs={12}>
                      <Box
                        borderRadius={3}
                        border={1}
                        mt={2}
                        borderColor={"#33588A"}
                        p={2}
                        display={"flex"}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                      >
                        <Typography color={"#BAD3F5"}>
                          Event will end in
                        </Typography>
                        <Typography fontSize={32} color={"#BAD3F5"}>
                          {(() => {
                            // console.log('Rendering countdown:', countdown);
                            if (
                              countdown.hours === 0 &&
                              countdown.minutes === 0 &&
                              countdown.seconds === 0
                            ) {
                              return "00 : 00 : 00";
                            }
                            return `${countdown.hours
                              .toString()
                              .padStart(2, "0")} : ${countdown.minutes
                              .toString()
                              .padStart(2, "0")} : ${countdown.seconds
                              .toString()
                              .padStart(2, "0")}`;
                          })()}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item width={280}>
                <Box
                  height={"100%"}
                  pl={3}
                  position={"relative"}
                  sx={{
                    "&::before": {
                      content: '""',
                      display: "block",
                      height: "100%",
                      width: "1px",
                      border: "1px dashed #33588A",
                      position: "absolute",
                      left: 0,
                      top: "54%",
                      transform: "translate(0px, -50%)",
                    },
                  }}
                >
                  <Grid item container spacing={2}>
                    <Grid item xs={12}>
                      <Box
                        borderRadius={3}
                        border={1}
                        borderColor={"#33588A"}
                        p={2}
                      >
                        <Box display={"flex"} justifyContent={"center"} mb={1}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="47"
                            height="47"
                            viewBox="0 0 47 47"
                            fill="none"
                          >
                            <path
                              d="M31.9382 20.3057C35.6386 20.3057 38.6484 17.2953 38.6484 13.5948C38.6484 9.89438 35.6386 6.88391 31.9382 6.88391C28.2377 6.88391 25.2266 9.89438 25.2266 13.5948C25.2266 17.2953 28.2377 20.3057 31.9382 20.3057Z"
                              fill="#BAD3F5"
                            />
                            <path
                              d="M31.9434 22.4619C30.5902 22.4619 29.2958 22.6788 28.0956 23.1063C27.3488 23.3723 26.9585 24.1949 27.2238 24.9424C27.4674 25.625 28.172 25.9832 28.8644 25.8425C28.9303 25.829 28.9961 25.8374 29.0614 25.8142C29.95 25.4977 30.9201 25.3369 31.9434 25.3369C34.3861 25.3369 36.6799 26.2852 38.4009 28.0063C40.122 29.728 41.071 32.0219 41.071 34.4652C41.071 34.9993 40.8632 35.5005 40.4856 35.8788C40.108 36.2557 39.6124 36.4635 39.0874 36.4635H31.3538C31.2527 36.4635 31.164 36.5012 31.0687 36.5211C30.416 36.6571 29.9163 37.2082 29.9163 37.901C29.9163 38.6949 30.5593 39.3385 31.3538 39.3385H39.0874C40.3803 39.3385 41.5988 38.831 42.5183 37.9115C43.4392 36.9913 43.946 35.7679 43.946 34.4652C43.946 31.254 42.698 28.2386 40.4337 25.9735C38.1693 23.7092 35.1539 22.4619 31.9434 22.4619Z"
                              fill="#BAD3F5"
                            />
                            <path
                              d="M15.7043 20.3057C19.4048 20.3057 22.416 17.2953 22.416 13.5948C22.416 9.89438 19.4048 6.88391 15.7043 6.88391C12.0039 6.88391 8.99414 9.89438 8.99414 13.5948C8.99414 17.2953 12.0039 20.3057 15.7043 20.3057Z"
                              fill="#BAD3F5"
                            />
                            <path
                              d="M8.55908 39.3336H22.8513C25.5325 39.3336 27.7141 37.1521 27.7141 34.4708C27.7141 27.849 22.3262 22.4619 15.7045 22.4619C9.0827 22.4619 3.69629 27.849 3.69629 34.4708C3.69629 37.1521 5.87781 39.3336 8.55908 39.3336Z"
                              fill="#BAD3F5"
                            />
                          </svg>
                        </Box>
                        <Typography
                          fontSize={32}
                          color={"#BAD3F5"}
                          textAlign={"center"}
                        >
                          {e?.eventAttendee?.length || 0}
                        </Typography>
                        <Typography
                          fontSize={14}
                          color={"#BAD3F5"}
                          textAlign={"center"}
                        >
                          Total Attendees
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        borderRadius={3}
                        border={1}
                        borderColor={"#33588A"}
                        p={2}
                        position={"relative"}
                      >
                        <Box display={"flex"} justifyContent={"center"} mb={1}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="47"
                            height="47"
                            viewBox="0 0 47 47"
                            fill="none"
                          >
                            <g clip-path="url(#clip0_159_549)">
                              <path
                                d="M32.6009 20.6357C30.2564 20.6357 28.06 21.2811 26.1789 22.4024C25.7655 22.1294 25.2828 21.9825 24.7769 21.9825H23.5611C25.8897 19.698 27.3369 16.5181 27.3369 13.0061C27.3369 6.06919 21.6933 0.425659 14.7563 0.425659C7.81941 0.425659 2.17578 6.06919 2.17578 13.0061C2.17578 16.5181 3.62298 19.698 5.95155 21.9825H4.73579C3.32434 21.9825 2.17596 23.1308 2.17596 24.5423V26.387C2.17596 27.1325 2.49634 27.8046 3.00666 28.2729C2.49634 28.7412 2.17596 29.4133 2.17596 30.1588V32.0036C2.17596 32.7491 2.49634 33.4212 3.00666 33.8895C2.49634 34.3578 2.17596 35.0299 2.17596 35.7754V37.6201C2.17596 38.3657 2.49634 39.0378 3.00675 39.5061C2.49643 39.9743 2.17596 40.6465 2.17596 41.392V43.2367C2.17596 44.6482 3.32425 45.7965 4.73579 45.7965H24.7769C25.7167 45.7965 26.5646 45.2696 27.0091 44.4831C28.6946 45.3229 30.5934 45.7964 32.6009 45.7964C39.5378 45.7964 45.1815 40.1529 45.1815 33.2159C45.1815 26.279 39.5378 20.6357 32.6009 20.6357ZM3.52344 13.0061C3.52344 6.81229 8.5625 1.77332 14.7563 1.77332C20.9502 1.77332 25.9892 6.81229 25.9892 13.0061C25.9892 19.1999 20.9502 24.2389 14.7563 24.2389C8.5625 24.239 3.52344 19.1999 3.52344 13.0061ZM3.52371 24.5424C3.52371 23.8741 4.06744 23.3302 4.73588 23.3302H7.57602C9.61359 24.7517 12.0891 25.5867 14.7563 25.5867C17.4236 25.5867 19.8991 24.7517 21.9366 23.3302H24.7769C24.7933 23.3302 24.8095 23.332 24.8258 23.3327C23.3748 24.4767 22.1813 25.9331 21.3464 27.5993H4.73579C4.06744 27.5993 3.52362 27.0555 3.52362 26.3872L3.52371 24.5424ZM3.52371 35.7756C3.52371 35.1072 4.06744 34.5635 4.73588 34.5635H20.0928C20.2553 36.0832 20.6897 37.5226 21.346 38.8325H4.73579C4.06744 38.8325 3.52362 38.2887 3.52362 37.6203L3.52371 35.7756ZM32.6009 44.449C26.4071 44.449 21.3681 39.4099 21.3681 33.2162C21.3681 27.0224 26.4071 21.9834 32.6009 21.9834C38.7947 21.9834 43.8338 27.0224 43.8338 33.2162C43.8338 39.4099 38.7947 44.449 32.6009 44.449ZM14.7563 22.3137C19.8886 22.3137 24.0639 18.1384 24.0639 13.0061C24.0639 7.87389 19.8886 3.69867 14.7563 3.69867C9.6241 3.69867 5.44879 7.87398 5.44879 13.0061C5.44879 18.1383 9.62419 22.3137 14.7563 22.3137ZM14.0825 8.18906V7.404C14.0825 7.03187 14.3842 6.73017 14.7563 6.73017C15.1285 6.73017 15.4302 7.03187 15.4302 7.404V8.23057C16.6308 8.53226 17.5224 9.62081 17.5224 10.9137C17.5224 11.2859 17.2207 11.5876 16.8486 11.5876C16.4765 11.5876 16.1748 11.2859 16.1748 10.9137C16.1748 10.1316 15.5384 9.49521 14.7562 9.49521H14.5612C13.779 9.49521 13.1426 10.1316 13.1426 10.9137C13.1426 11.9608 14.0119 12.3324 14.7562 12.3324C16.4721 12.3324 17.7174 13.4957 17.7174 15.0986C17.7174 16.4606 16.7279 17.5959 15.43 17.8234V18.6083C15.43 18.9805 15.1283 19.2822 14.7562 19.2822C14.384 19.2822 14.0823 18.9805 14.0823 18.6083V17.7819C12.8817 17.4802 11.99 16.3916 11.99 15.0987C11.99 14.7265 12.2916 14.4248 12.6638 14.4248C13.0359 14.4248 13.3376 14.7265 13.3376 15.0987C13.3376 15.8809 13.974 16.5172 14.7562 16.5172H14.9512C15.7335 16.5172 16.3698 15.8808 16.3698 15.0986C16.3698 14.0515 15.5006 13.68 14.7562 13.68C13.0404 13.68 11.7951 12.5167 11.7951 10.9137C11.7951 9.55172 12.7845 8.41636 14.0825 8.18906ZM32.6009 23.9087C27.4687 23.9087 23.2934 28.084 23.2934 33.2162C23.2934 38.3483 27.4687 42.5237 32.6009 42.5237C37.7332 42.5237 41.9085 38.3483 41.9085 33.2162C41.9085 28.084 37.7332 23.9087 32.6009 23.9087ZM33.2748 38.0334V38.8183C33.2748 39.1905 32.9731 39.4921 32.601 39.4921C32.2289 39.4921 31.9272 39.1905 31.9272 38.8183V37.9918C30.7264 37.6903 29.8347 36.6016 29.8347 35.3087C29.8347 34.9364 30.1364 34.6348 30.5085 34.6348C30.8807 34.6348 31.1824 34.9364 31.1824 35.3087C31.1824 36.0908 31.8187 36.7272 32.6009 36.7272H32.796C33.5782 36.7272 34.2146 36.0908 34.2146 35.3086C34.2146 34.2616 33.3454 33.8901 32.6009 33.8901C30.8851 33.8901 29.6398 32.7267 29.6398 31.1238C29.6398 29.7618 30.6292 28.6264 31.9272 28.3991V27.6141C31.9272 27.242 32.2289 26.9403 32.601 26.9403C32.9731 26.9403 33.2748 27.242 33.2748 27.6141V28.4406C34.4755 28.7423 35.3671 29.8309 35.3671 31.1238C35.3671 31.4961 35.0654 31.7977 34.6933 31.7977C34.3212 31.7977 34.0195 31.4961 34.0195 31.1238C34.0195 30.3417 33.3831 29.7054 32.6009 29.7054H32.406C31.6238 29.7054 30.9874 30.3417 30.9874 31.1238C30.9874 32.1709 31.8566 32.5425 32.6009 32.5425C34.3168 32.5425 35.5623 33.7058 35.5623 35.3086C35.5623 36.6706 34.5727 37.806 33.2748 38.0334Z"
                                fill="#BAD3F5"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_159_549">
                                <rect
                                  width="46"
                                  height="46"
                                  fill="white"
                                  transform="translate(0.678711 0.111206)"
                                />
                              </clipPath>
                            </defs>
                          </svg>
                        </Box>
                        <Typography
                          fontSize={32}
                          color={"#BAD3F5"}
                          textAlign={"center"}
                        >
                          {"Votes- " + e?.revenueDetail?.totalVotes || 0}
                        </Typography>
                        <Typography
                          fontSize={14}
                          color={"#BAD3F5"}
                          textAlign={"center"}
                        >
                          Votes count & earnings
                        </Typography>
                        <Box
                          fontSize={12}
                          color={"#BAD3F5"}
                          position={"absolute"}
                          right={8}
                          top={8}
                        >
                          <img src="/images/star-medal.svg" alt="" /> Vote :{" "}
                          {e?.revenueDetail?.totalVotes || 0}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Box mt={3} maxWidth={1000} mx={"auto"}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="basic tabs example"
                sx={{
                  border: "1px solid #33588A",
                  borderRadius: "999px",
                  "& button": {
                    flex: 1,
                    "&.Mui-selected": {
                      borderRadius: "999px",
                      backgroundColor: "#0057FF",
                      color: "#fff !important",
                    },
                  },
                  "& .MuiTabs-indicator": {
                    display: "none",
                  },
                }}
              >
                <Tab label="Invited Dj" {...a11yProps(0)} />
                <Tab label="Accepted Dj" {...a11yProps(1)} />
                <Tab label="Song request" {...a11yProps(2)} />
              </Tabs>
            </Box>
            <CustomTabPanel value={value} index={0}>
              <Grid container spacing={2}>
                {e?.invitedDj?.map((dj, index) => (
                  <Grid item xs={12} sm={4} key={index}>
                    <Box
                      borderRadius={2}
                      border={"1px solid #33588A"}
                      p={2}
                      sx={{ backgroundColor: "#112E55" }}
                    >
                      <Grid item container spacing={2} alignItems={"center"}>
                        <Grid item width={96}>
                          <Box
                            width={80}
                            height={70}
                            borderRadius={2}
                            overflow={"hidden"}
                          >
                            <CardMedia
                              component={"img"}
                              sx={{ height: "100%" }}
                              src={`${process.env.REACT_APP_S3_URL}${
                                dj?.djDetail?.profilePicture ||
                                dj?.profilePicture ||
                                e?.logo||
                                '/images/musical-note.jpg'
                              }`}
                            />
                          </Box>
                        </Grid>
                        <Grid item width={"calc(100% - 96px)"}>
                          <Typography fontWeight={500} color={"#fff"}>
                            {dj?.djDetail?.firstName || dj?.firstName || "DJ"}{" "}
                            {dj?.djDetail?.lastName || dj?.lastName || ""}
                          </Typography>
                          <Typography fontSize={12} color={"#BAD3F5"}>
                            Status: {dj?.status || "INVITED"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                ))}
                {(!e?.invitedDj || e?.invitedDj?.length === 0) && (
                  <Grid item xs={12}>
                    <Typography color={"#fff"} textAlign={"center"}>
                      No invited DJs found
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
              <Grid container spacing={2}>
                {e?.acceptedDj?.map((dj, index) => (
                  <Grid item xs={12} sm={4} key={index}>
                    <Box
                      borderRadius={2}
                      border={"1px solid #33588A"}
                      p={2}
                      sx={{ backgroundColor: "#112E55" }}
                    >
                      <Grid item container spacing={2} alignItems={"center"}>
                        <Grid item width={96}>
                          <Box
                            width={80}
                            height={70}
                            borderRadius={2}
                            overflow={"hidden"}
                          >
                            <CardMedia
                              component={"img"}
                              sx={{ height: "100%" }}
                              src={`${process.env.REACT_APP_S3_URL}${
                                dj?.djDetail?.profilePicture ||
                                dj?.profilePicture ||
                                e?.logo||
                                '/images/musical-note.jpg'
                              }`}
                            />
                          </Box>
                        </Grid>
                        <Grid item width={"calc(100% - 96px)"}>
                          <Typography fontWeight={500} color={"#fff"}>
                            {dj?.djDetail?.firstName || dj?.firstName || "DJ"}{" "}
                            {dj?.djDetail?.lastName || dj?.lastName || ""}
                          </Typography>
                          <Typography fontSize={12} color={"#BAD3F5"}>
                            Status: {dj?.status || "ACCEPTED"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                ))}
                {(!e?.acceptedDj || e?.acceptedDj?.length === 0) && (
                  <Grid item xs={12}>
                    <Typography color={"#fff"} textAlign={"center"}>
                      No accepted DJs found
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={2}>
              <Grid container spacing={2}>
                {e?.requestedSong?.map((song, index) => (
                  <Grid item xs={12} sm={4} key={index}>
                    <Box
                      borderRadius={2}
                      border={"1px solid #33588A"}
                      p={2}
                      sx={{ backgroundColor: "#112E55" }}
                    >
                      <Grid item container spacing={2} alignItems={"center"}>
                        <Grid item width={96}>
                          <Box
                            width={80}
                            height={70}
                            borderRadius={2}
                            overflow={"hidden"}
                          >
                            {/* <CardMedia component={'img'} sx={{height:'100%'}} src={`${process.env.REACT_APP_S3_URL}${song?.image || song?.albumImage || e?.logo}`}/> */}
                            <CardMedia
                              component={"img"}
                              sx={{ height: "100%" }}
                              src={song?.songImg? song?.songImg : '/images/musical-note.jpg'}
                            
                                onError={(e) => {
                                  e.currentTarget.src = "/images/musical-note.png";
                                }}
                            
                            />
                          </Box>
                        </Grid>
                        <Grid item width={"calc(100% - 96px)"}>
                          <Typography fontWeight={500} color={"#fff"}>
                            {song?.name || song?.title || "Unknown Song"}
                          </Typography>
                          <Typography fontSize={12} color={"#BAD3F5"} mb={1}>
                            by{" "}
                            {song?.artist ||
                              song?.artistName ||
                              "Unknown Artist"}
                          </Typography>
                          <a
                            href={song?.externalUrls}
                            target="_blank"
                            rel="noreferrer noopener"
                            style={{
                              fontSize: 14,
                              color: "#0057FF",
                              display: "inline-block",
                              maxWidth: 200, // 🔹 adjust width as needed
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              verticalAlign: "middle",
                            }}
                            title={
                              song?.spotifyUrl ||
                              song?.url ||
                              song?.externalUrls
                            } // hover full text
                          >
                            {song?.spotifyUrl ||
                              song?.url ||
                              song?.externalUrls ||
                              "No URL available"}
                          </a>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                ))}
                {(!e?.requestedSong || e?.requestedSong?.length === 0) && (
                  <Grid item xs={12}>
                    <Typography color={"#fff"} textAlign={"center"}>
                      No song requests found
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CustomTabPanel>
          </Box>
          {/* <Box mt={3} maxWidth={1000} mx={'auto'} pb={3}>
                        <Grid container justifyContent={'flex-end'}>
                            <Button variant='contained' color='error' sx={{textTransform:'capitalize'}}>Close Event</Button>
                        </Grid>
                    </Box> */}
        </Box>
      )}
      {!state.loading && state.error && (
        <Box maxWidth={1000} mx={"auto"} mt={3}>
          <Box
            borderRadius={3}
            border={1}
            borderColor={"#ff6b6b"}
            p={3}
            sx={{ backgroundColor: "#2d1b1b" }}
          >
            <Typography color={"#ff6b6b"} fontSize={18} fontWeight={600} mb={2}>
              Error Loading Event Details
            </Typography>
            <Typography color={"#ff6b6b"} fontSize={14} mb={2}>
              {state.error}
            </Typography>
            <Box display={"flex"} gap={2}>
              <Button
                variant="contained"
                onClick={() => {
                  const eventId = params.get("id");
                  if (eventId) {
                    fetchEventDetails(eventId);
                  }
                }}
                sx={{
                  backgroundColor: "#0057FF",
                  "&:hover": { backgroundColor: "#0047CC" },
                }}
              >
                Retry
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate("/user/event-list")}
                sx={{
                  backgroundColor: "#ff6b6b",
                  "&:hover": { backgroundColor: "#ff5252" },
                }}
              >
                Back to Event List
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      {!state.loading && !e && !state.error && (
        <Box maxWidth={1000} mx={"auto"} mt={3}>
          <Box
            borderRadius={3}
            border={1}
            borderColor={"#33588A"}
            p={3}
            sx={{ backgroundColor: "#112E55" }}
          >
            <Typography color={"#BAD3F5"} fontSize={18} fontWeight={600} mb={2}>
              No Event Data Found
            </Typography>
            <Typography color={"#BAD3F5"} fontSize={14} mb={2}>
              The event you're looking for doesn't exist or has been removed.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/user/event-list")}
              sx={{
                backgroundColor: "#0057FF",
                "&:hover": { backgroundColor: "#0047CC" },
              }}
            >
              Back to Event List
            </Button>
          </Box>
        </Box>
      )}
    </div>
  );
}
